import React from 'react';
import { AlertTriangle, Check, X } from 'lucide-react';
import { Vendor, VendorComplianceStatus } from '../../types';
import { normalizeComplianceStatus } from '../../utils/vendorCompliance';

interface VendorComplianceIconProps {
  vendor: Vendor;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
}

const VendorComplianceIcon: React.FC<VendorComplianceIconProps> = ({ vendor, onClick, className = '' }) => {
  const label = vendor.complianceStatusLabel || 'Set up compliance';
  const status = normalizeComplianceStatus(vendor.complianceStatus, vendor.complianceStatusLabel);

  const iconConfig = (() => {
    if (status === VendorComplianceStatus.Compliant) {
      return {
        bg: 'bg-green-100 dark:bg-green-900/40',
        ring: 'ring-green-200 dark:ring-green-800',
        icon: <Check className="h-3.5 w-3.5 text-green-700 dark:text-green-300" aria-hidden="true" strokeWidth={2.5} />,
      };
    }

    if (status === VendorComplianceStatus.ExpiringSoon) {
      return {
        bg: 'bg-amber-100 dark:bg-amber-900/40',
        ring: 'ring-amber-200 dark:ring-amber-800',
        icon: <AlertTriangle className="h-3.5 w-3.5 text-amber-700 dark:text-amber-300" aria-hidden="true" strokeWidth={2.5} />,
      };
    }

    if (status === VendorComplianceStatus.NonCompliant) {
      return {
        bg: 'bg-red-100 dark:bg-red-900/40',
        ring: 'ring-red-200 dark:ring-red-800',
        icon: <X className="h-3.5 w-3.5 text-red-700 dark:text-red-300" aria-hidden="true" strokeWidth={2.5} />,
      };
    }

    return {
      bg: 'bg-gray-100 dark:bg-gray-800',
      ring: 'ring-gray-200 dark:ring-gray-700',
      icon: <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400">—</span>,
    };
  })();

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex h-7 w-7 items-center justify-center rounded-full ring-1 ${iconConfig.bg} ${iconConfig.ring} hover:opacity-90 transition-opacity ${className}`}
      title={label}
      aria-label={label}
    >
      {iconConfig.icon}
    </button>
  );
};

export default VendorComplianceIcon;
